import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, LinearProgress, Table, TableHead, TableRow,
  TableCell, Paper, Rating,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  BEAUTY_SERVICE_PROVIDERS_QUERY,
  CREATE_BEAUTY_SERVICE_PROVIDER_MUTATION, UPDATE_BEAUTY_SERVICE_PROVIDER_MUTATION, DELETE_BEAUTY_SERVICE_PROVIDER_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const EMPTY_FORM = { name:'', contactName:'', phone:'', email:'', city:'', address:'', description:'', serviceTypes:[], priceMin:'', priceMax:'', rating:'', instagramUrl:'', websiteUrl:'', portfolioUrl:'', notes:'' };

export default function AdminBeautyServicesPage() {
  const [error, setError]     = useState('');
  const [dialog, setDialog]   = useState(false);
  const [editProv, setEditProv] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [confirmDel, setConfirmDel] = useState(null);

  const { data, loading, refetch } = useQuery(BEAUTY_SERVICE_PROVIDERS_QUERY, { fetchPolicy:'cache-and-network' });
  const providers = data?.beautyServiceProviders || [];

  const { options: serviceTypeOpts } = useLov('beauty_service_type');

  const [createProv] = useMutation(CREATE_BEAUTY_SERVICE_PROVIDER_MUTATION, { onCompleted:()=>{ setDialog(false); setForm(EMPTY_FORM); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateProv] = useMutation(UPDATE_BEAUTY_SERVICE_PROVIDER_MUTATION, { onCompleted:()=>{ setDialog(false); setEditProv(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteProv] = useMutation(DELETE_BEAUTY_SERVICE_PROVIDER_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetch(); } });

  const openEdit = (p) => {
    setEditProv(p);
    setForm({ name:p.name||'', contactName:p.contactName||'', phone:p.phone||'', email:p.email||'', city:p.city||'', address:p.address||'', description:p.description||'', serviceTypes:p.serviceTypes||[], priceMin:p.priceMin||'', priceMax:p.priceMax||'', rating:p.rating||'', instagramUrl:p.instagramUrl||'', websiteUrl:p.websiteUrl||'', portfolioUrl:p.portfolioUrl||'', notes:p.notes||'' });
    setDialog(true);
  };
  const handleSave = () => {
    const vars = { ...form, priceMin:form.priceMin?parseFloat(form.priceMin):undefined, priceMax:form.priceMax?parseFloat(form.priceMax):undefined, rating:form.rating?parseFloat(form.rating):undefined };
    editProv ? updateProv({ variables:{ id:editProv.id, ...vars } }) : createProv({ variables:vars });
  };
  const toggleServiceType = (t) => setForm(f=>({ ...f, serviceTypes: f.serviceTypes.includes(t) ? f.serviceTypes.filter(s=>s!==t) : [...f.serviceTypes,t] }));

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin — Beauty Service Providers</Typography>
          <Typography variant="body2" color="text.secondary">Manage all bridal & beauty artist profiles visible to users</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditProv(null); setForm(EMPTY_FORM); setDialog(true); }}>Add Provider</Button>
      </Stack>

      <ContractedVendorsSection category="beauty" />
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{mb:2}}/>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell><TableCell>City</TableCell><TableCell>Services</TableCell>
            <TableCell>Price Range</TableCell><TableCell>Rating</TableCell><TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {providers.map(p=>(
            <TableRow key={p.id}>
              <TableCell>
                <Typography fontWeight={600}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary">{p.contactName}</Typography>
              </TableCell>
              <TableCell>{p.city||'—'}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {(p.serviceTypes||[]).map(t=><Chip key={t} size="small" label={t.replace('_',' ')} variant="outlined"/>)}
                </Stack>
              </TableCell>
              <TableCell>{p.priceMin!=null?`₹${Number(p.priceMin).toLocaleString('en-IN')}`:'—'} – {p.priceMax!=null?`₹${Number(p.priceMax).toLocaleString('en-IN')}`:'—'}</TableCell>
              <TableCell><Rating value={parseFloat(p.rating)||0} readOnly size="small" precision={0.5}/></TableCell>
              <TableCell>
                <IconButton size="small" onClick={()=>openEdit(p)}><EditIcon fontSize="small"/></IconButton>
                <IconButton size="small" color="error" onClick={()=>setConfirmDel(p.id)}><DeleteIcon fontSize="small"/></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {providers.length===0 && !loading && <TableRow><TableCell colSpan={6}><Typography textAlign="center" color="text.secondary" py={3}>No providers yet.</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>

      {/* Dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editProv?'Edit Provider':'Add Provider'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Provider Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Contact Name" value={form.contactName} onChange={e=>setForm(f=>({...f,contactName:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Phone" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="City" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{mb:0.5,display:'block'}}>Service Types (select all that apply)</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {serviceTypeOpts.map(t=>(
                <Chip key={t.value} label={t.label} onClick={()=>toggleServiceType(t.value)}
                  color={form.serviceTypes.includes(t.value)?'primary':'default'}
                  variant={form.serviceTypes.includes(t.value)?'filled':'outlined'}/>
              ))}
            </Stack>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={4}><TextField label="Min Price (₹)" type="number" value={form.priceMin} onChange={e=>setForm(f=>({...f,priceMin:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={4}><TextField label="Max Price (₹)" type="number" value={form.priceMax} onChange={e=>setForm(f=>({...f,priceMax:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={4}><TextField label="Rating (0-5)" type="number" value={form.rating} onChange={e=>setForm(f=>({...f,rating:e.target.value}))} inputProps={{min:0,max:5,step:0.1}} fullWidth/></Grid>
          </Grid>
          <TextField label="Instagram URL" value={form.instagramUrl} onChange={e=>setForm(f=>({...f,instagramUrl:e.target.value}))} fullWidth/>
          <TextField label="Website URL" value={form.websiteUrl} onChange={e=>setForm(f=>({...f,websiteUrl:e.target.value}))} fullWidth/>
          <TextField label="Portfolio URL" value={form.portfolioUrl} onChange={e=>setForm(f=>({...f,portfolioUrl:e.target.value}))} fullWidth/>
          <TextField label="Description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} multiline rows={3} fullWidth/>
          <TextField label="Notes" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Provider?</DialogTitle>
        <DialogContent><Typography>This will remove the provider. Existing bookings will remain.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteProv({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
